import React from "react";
import { createRoot } from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import App from "./App";
import { msalInstance, msalReady } from "./auth/msalConfig";
import { isAppRoute } from "./utils/postLoginRedirect";
import { captureAcquisition } from "./utils/acquisition";
import "./styles/tailwind.css";

// Acquisition source (utm_* and ref on the landing URL) is captured FIRST: before
// handleRedirectPromise below reads or rewrites the URL, and before any
// routing can drop the query string. One localStorage record, first touch
// wins, written only with consent (held in memory until "Godta"); storage
// failures are swallowed inside — boot never depends on it.
captureAcquisition();

// Get the container for the React root
const container = document.getElementById('root');
const root = createRoot(container);

// Boot-failure state: MSAL never initialized, so nothing from the normal app
// can be assumed. Inline styles only — the CSS bundle is usually loaded by
// this point, but this screen must not depend on it. Hexes mirror the Coord
// tokens (tailwind.config.js); button mirrors .btn-primary (dark text on peach).
const BootError = () => (
  <div
    role="alert"
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      padding: '0 1.5rem',
      textAlign: 'center',
      background: '#FCFCF9', // background.DEFAULT
      fontFamily: '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      WebkitFontSmoothing: 'antialiased',
    }}
  >
    <h1
      style={{
        margin: 0,
        fontFamily: '"Source Serif 4", Georgia, serif', // font-display
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#2D2A26', // text.primary
      }}
    >
      Vi fikk ikke startet Coord
    </h1>
    <p
      style={{
        margin: 0,
        maxWidth: '26rem',
        fontSize: '1rem',
        lineHeight: '1.5rem',
        color: '#4A4641', // text.secondary
      }}
    >
      Noe gikk galt under innlastingen. Det er ikke noe du har gjort feil
      — prøv igjen om et lite øyeblikk.
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      style={{
        marginTop: '0.75rem',
        padding: '0.75rem 2rem', // 48px tap height
        border: 'none',
        borderRadius: '0.5rem', // rounded-lg
        background: '#D97757', // primary.500
        color: '#2D2A26', // text.primary
        fontFamily: 'inherit',
        fontSize: '1rem',
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      Prøv igjen
    </button>
  </div>
);

// Initialize MSAL, then process any redirect response, before rendering the app
msalReady
  .then(() => msalInstance.handleRedirectPromise())
  .then((response) => {
    // If we just came back from a redirect login, set the returned account active.
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
      // Land on '/' so App.js routing decides dashboard vs onboarding,
      // instead of stranding the user on the public page they logged in
      // from (e.g. /privacy). Runs before the Router mounts, so a plain
      // history rewrite is enough — and it only fires on a fresh redirect
      // response from a non-app route, so it cannot loop.
      if (!isAppRoute(window.location.pathname)) {
        window.history.replaceState(null, "", "/");
      }
    } else {
      // Otherwise, make sure any cached account is active
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  })
  .then(() => {
    // Render the app after MSAL is initialized
    root.render(
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    );
  })
  .catch(error => {
    // No server-visible channel exists at boot (no telemetry SDK; GA4 is
    // consent-gated and loads after App mounts) — console is the only sink.
    console.error('[MSAL] Initialization failed:', error);

    // Never dead-end on the static boot shell: render a calm error state with
    // one user-initiated retry. No auto-retry — it could spin against a
    // failure that won't clear. We also deliberately ship NO watchdog for an
    // MSAL hang that never rejects: with zero frontend telemetry we cannot
    // know how often that happens, so this is a blind choice made knowingly,
    // not an oversight.
    root.render(<BootError />);
  });
